import { useCallback, useState } from 'preact/hooks';
import { CALLINGS, CONTRACTS, FACTION_NAMES, type FactionId } from '@stillsun/shared';
import { api } from '../lib/api.js';
import { useFetch } from '../lib/useFetch.js';
import type { ContractStat, MarkStat, ProgressionTelemetry } from '../lib/types.js';
import { StatTile } from '../components/StatTile.js';
import { BarChart, DonutChart } from '../components/Chart.js';
import { formatPct } from '../lib/format.js';

// Categorical palette (one hue per Calling), reusing the theme tokens the rest of the dashboard
// already draws from — never an arbitrary rainbow.
const CALLING_COLORS: Record<number, string> = {
  1: 'var(--amber)',
  2: 'var(--info)',
  3: 'var(--violet)',
  4: 'var(--ok)',
  0: 'var(--bone-faint)',
};

type MarkSortKey = 'name' | 'bronze' | 'amber' | 'hymn';

function markSortValue(m: MarkStat, key: MarkSortKey): string | number {
  return key === 'name' ? m.name : m[key];
}

export function Progression() {
  const fetchData = useCallback(
    () => api.get<ProgressionTelemetry>('/api/admin/telemetry/progression'),
    [],
  );
  const { data, error, loading } = useFetch(fetchData, []);

  const [markSort, setMarkSort] = useState<{ key: MarkSortKey; dir: 'asc' | 'desc' }>({
    key: 'hymn',
    dir: 'desc',
  });
  const toggleMarkSort = (key: MarkSortKey) => {
    setMarkSort((s) =>
      s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' },
    );
  };

  const callingSlices = [1, 2, 3, 4, 0].map((id) => ({
    label: id === 0 ? 'Unaligned' : (CALLINGS[id]?.name ?? `Calling ${id}`),
    value: data?.callings[String(id)] ?? 0,
    color: CALLING_COLORS[id]!,
  }));

  const sortedMarks = [...(data?.marks ?? [])].sort((a, b) => {
    const dir = markSort.dir === 'asc' ? 1 : -1;
    const av = markSortValue(a, markSort.key);
    const bv = markSortValue(b, markSort.key);
    if (typeof av === 'string' && typeof bv === 'string') return av.localeCompare(bv) * dir;
    return ((av as number) - (bv as number)) * dir;
  });

  const contractTitle = (c: ContractStat) => CONTRACTS.find((t) => t.id === c.id)?.title ?? c.id;
  const factionIds = Object.keys(data?.factions ?? {}) as FactionId[];

  return (
    <div>
      <h1 class="page-title">Progression</h1>
      {error && <div class="error-banner">{error}</div>}

      <div class="tiles">
        <StatTile label="Wick in circulation" value={data?.economy.wickInCirculation ?? '—'} />
        <StatTile label="Wick earned (7d)" value={data?.economy.wickEarned7d ?? '—'} />
        <StatTile
          label="Wick spent (7d)"
          value={data?.economy.wickSpent7d ?? '—'}
          sub="Pending a spend ledger"
        />
        <StatTile
          label="Items crafted (7d)"
          value={data?.economy.itemsCrafted7d ?? '—'}
          sub="Pending a craft ledger"
        />
      </div>

      <div class="charts-grid">
        <div class="card">
          <DonutChart title="Calling share (characters)" data={callingSlices} />
        </div>
        <div class="card">
          <BarChart
            title="Level distribution"
            points={(data?.levels ?? []).map((b) => ({ label: b.bucket, value: b.count }))}
          />
        </div>
      </div>

      <div class="card" style={{ marginBottom: 18 }}>
        <div class="section-label" style={{ marginTop: 0 }}>
          Marks
        </div>
        {!loading && sortedMarks.length === 0 && <div class="empty">No Marks data yet.</div>}
        {sortedMarks.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table class="grid">
              <thead>
                <tr>
                  <MarkHeader
                    label="Mark"
                    sortKey="name"
                    current={markSort}
                    onSort={toggleMarkSort}
                  />
                  <MarkHeader
                    label="Bronze"
                    sortKey="bronze"
                    current={markSort}
                    onSort={toggleMarkSort}
                  />
                  <MarkHeader
                    label="Amber"
                    sortKey="amber"
                    current={markSort}
                    onSort={toggleMarkSort}
                  />
                  <MarkHeader
                    label="Hymn"
                    sortKey="hymn"
                    current={markSort}
                    onSort={toggleMarkSort}
                  />
                </tr>
              </thead>
              <tbody>
                {sortedMarks.map((m) => (
                  <tr key={m.id}>
                    <td>{m.name}</td>
                    <td>{m.bronze}</td>
                    <td>{m.amber}</td>
                    <td>{m.hymn}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div class="card" style={{ marginBottom: 18 }}>
        <div class="section-label" style={{ marginTop: 0 }}>
          Contracts (last 14 days)
        </div>
        <div class="hint" style={{ marginTop: -4, marginBottom: 8 }}>
          "Completed" is an upper bound for past days — it re-checks progress against each player's
          current counter, which only ever increases.
        </div>
        {!loading && (data?.contracts.length ?? 0) === 0 && (
          <div class="empty">No contract data yet.</div>
        )}
        {(data?.contracts.length ?? 0) > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table class="grid">
              <thead>
                <tr>
                  <th>Contract</th>
                  <th>Offered</th>
                  <th>Completed</th>
                  <th>Claimed</th>
                  <th>Completion</th>
                </tr>
              </thead>
              <tbody>
                {data!.contracts.map((c) => (
                  <tr key={c.id}>
                    <td>{contractTitle(c)}</td>
                    <td>{c.offered}</td>
                    <td>{c.completed}</td>
                    <td>{c.claimed}</td>
                    <td>{c.offered > 0 ? formatPct(c.completed / c.offered) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div class="section-label">Factions</div>
      <div class="tiles" style={{ marginBottom: 18 }}>
        {factionIds.map((fid) => {
          const f = data!.factions[fid]!;
          return (
            <StatTile
              key={fid}
              label={FACTION_NAMES[fid] ?? fid}
              value={f.avgRep}
              sub={`avg rep · T1 ${f.tier1} · T2 ${f.tier2} · T3 ${f.tier3} · T4 ${f.tier4}`}
            />
          );
        })}
      </div>

      <div class="card">
        <div class="section-label" style={{ marginTop: 0 }}>
          Top Wakers
        </div>
        {!loading && (data?.topWakers.length ?? 0) === 0 && <div class="empty">No Wakers yet.</div>}
        {(data?.topWakers.length ?? 0) > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table class="grid">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Level</th>
                  <th>Calling</th>
                  <th>Extracts</th>
                  <th>Kills</th>
                </tr>
              </thead>
              <tbody>
                {data!.topWakers.map((w, i) => (
                  <tr key={i}>
                    <td>{w.name}</td>
                    <td>{w.level}</td>
                    <td>{w.calling ? (CALLINGS[w.calling]?.name ?? w.calling) : 'Unaligned'}</td>
                    <td>{w.extracts}</td>
                    <td>{w.kills}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function MarkHeader({
  label,
  sortKey,
  current,
  onSort,
}: {
  label: string;
  sortKey: MarkSortKey;
  current: { key: MarkSortKey; dir: 'asc' | 'desc' };
  onSort: (key: MarkSortKey) => void;
}) {
  const active = current.key === sortKey;
  return (
    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => onSort(sortKey)}>
      {label}
      {active ? (current.dir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );
}
